import { ProTable, ProColumns, ProCoreActionType } from '@ant-design/pro-components';
import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { SettingOutlined, CloseOutlined } from '@ant-design/icons';
import { Button, Modal, Table, message, Popover } from 'antd';
import type { TableProps } from 'antd';
import { PaginationConfig } from '@/utils/commonConfig';
import { EIMIT, DATEFILDS } from '@/enums/pageEnum';
import { FilterComponet, ExportFile } from '@/components';
import { TableHeaderCol, QueryParams, FilterParams } from '@/facilities/model/user';
import { updateTableHeaderConfig, getTableHeaderConfig, getList } from '@api';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { parseQueryString, getTimeToString, parseMonth } from '@/utils/toolt';
import { get } from 'lodash-es';

export interface Params {
  [key: string]: string | number | undefined | boolean | [];
}
export type Key = React.Key;
export interface Props {
  bordered?: boolean;
  columns?: ProColumns[];
  tableColsRender?: ProColumns[];
  isShowCheckbox?: boolean;
  otherParams?: Params;
  key?: string | null | number;
  requestUrl?: string;
  isPagination?: boolean;
  rowKey?: any;
  size?: number;
  editTableHeaderUrl?: string;
  className?: string;
  tableName?: string;
  exportUrl?: string;
  tableHeight?: string | number;
  dateKeys?: QueryParams;
  footer?: React.ReactNode;
  filterTableFields?: FilterParams[];
  longPolling?: boolean; // 是否发起轮询请求
  isRefreshSelectedRows?: boolean;
  selectRowKeys?: React.Key[];
  isAddPathParams?: boolean;
  dataKey?: string;
  isAddParamsToUrl?: boolean;
  onCilickRow?: (record: any) => void;
  headerTitle?: ({
    node,
    dataSource,
  }: {
    node?: React.Ref<ProCoreActionType | undefined>;
    dataSource?: object[];
  }) => React.ReactNode;
  isDefaultToolBar?: boolean;
  rowSelection?:
    | (TableProps<any>['rowSelection'] & {
        alwaysShowAlert?: boolean;
      })
    | false;
  onChangeSelected?: (selectedRowKeys: Key[], selectedRows?: any) => void;
  getCurrentTable?: (datas: object[]) => void;
  filterSubmit?: (params: Params) => void;
  resetSubmit?: () => void;
  tableHeader?: (node?: React.Ref<ProCoreActionType | undefined>) => React.ReactNode;
}
export interface PropsRef {
  handleClick: () => React.Key[];
  getTableData: () => object[];
  afreshTableData: (cparams?: Params, isRest?: boolean) => void;
}

const BasicTable = forwardRef<PropsRef, Props>(
  (
    {
      otherParams,
      columns,
      tableColsRender = [],
      isShowCheckbox = true,
      isPagination = true,
      isAddPathParams = true,
      onChangeSelected,
      requestUrl,
      rowKey = 'id',
      size = 20,
      className,
      getCurrentTable,
      tableHeader,
      headerTitle,
      editTableHeaderUrl,
      isDefaultToolBar = true,
      tableName,
      tableHeight,
      longPolling = false,
      dateKeys,
      filterTableFields,
      filterSubmit,
      resetSubmit,
      rowSelection,
      exportUrl,
      bordered = false,
      isRefreshSelectedRows = true,
      isAddParamsToUrl = true,
      footer,
      onCilickRow,
      selectRowKeys = [],
      dataKey = 'data.body.content',
    },
    ref,
  ) => {
    const intervalTimer = useRef<any>(null);
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const [open, setOpen] = useState<boolean>(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
      selectRowKeys || [],
    );
    const [columnsKeys, setColumnsKeys] = useState<React.Key[]>([]);
    const [lastKeys, setLastKeys] = useState<React.Key[]>([]);
    const [tableColumns, setTableColumns] = useState<TableHeaderCol[]>([]);
    const [data, setData] = useState<object[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingConfirm, setLoadingConfirm] = useState(false);
    const [loadTableCols, setLoadTableCols] = useState(false);
    const tableRef = useRef<ProCoreActionType>(null);
    const action = useMemo(() => {
      return { fixed: 'right', width: '100px', align: 'center' };
    }, []);
    const [pageParams, setPageParams] = useState({
      current: 1,
      size,
      total: 0,
    });

    useEffect(() => {
      if (!editTableHeaderUrl || !isDefaultToolBar) return;
      setLoadTableCols(true);
      getTableHeaderConfig(editTableHeaderUrl)
        .then((res: any) => {
          const cols = res.data.body?.body;
          const keys: React.Key[] = [];
          cols.map((item: TableHeaderCol) => {
            if (item.view) {
              keys.push(item.key);
            }
          });
          setColumnsKeys(keys);
          setLastKeys(keys);
          setTableColumns(cols);
          setLoadTableCols(false);
        })
        .catch(() => {
          setLoadTableCols(false);
        });
    }, [editTableHeaderUrl, isDefaultToolBar]);

    const handleClick = () => {
      return selectedRowKeys;
    };

    // 获取表格筛选条件
    const getPathParams = useCallback(() => {
      const urlParms = parseQueryString(dateKeys);
      delete urlParms.date;
      return parseMonth(urlParms);
    }, [dateKeys]);

    const getData = useCallback(
      async (cparams?: Params, isRest?: boolean, isLoading = true) => {
        if (!requestUrl) return;
        setLoading(isLoading);
        const pathParms = isAddPathParams ? getPathParams() : {};
        const params = isRest
          ? { page: cparams?.page || 1, size: pageParams.size }
          : {
              ...pathParms,
              page: pageParams.current,
              size: pageParams.size,
              ...cparams,
              ...otherParams,
            };
        // console.log("table params: ", params);
        try {
          const res = await getList({ url: requestUrl, params });
          const body = get(res, dataKey, []);
          setData(body);
          if (isPagination) {
            setPageParams((prev) => {
              prev.total = res.data.body?.totalElements;
              if (cparams?.page) {
                prev.current = cparams?.page as number;
              }
              return prev;
            });
          }
        } catch (error: any) {
          message.error(error?.detail || error);
        }
        if (isRefreshSelectedRows) {
          setSelectedRowKeys([]);
          onChangeSelected?.([]);
        }
        setLoading(false);
      },
      [
        requestUrl,
        pageParams,
        isPagination,
        otherParams,
        getPathParams,
        isAddPathParams,
        dataKey,
        isRefreshSelectedRows,
        onChangeSelected,
      ],
    );

    const menuItemOnClick = useCallback(
      (val: any) => {
        if (val && val.detail.path !== pathname) return;
        getData({ page: 1 }, true);
        resetSubmit?.();
      },
      [getData, pathname, resetSubmit],
    );

    const reastParms = useCallback(
      (page: number, other?: Params) => {
        getData({ page, ...other });
      },
      [getData],
    );

    const handlePopState = () => {
      getData();
    };

    useEffect(() => {
      reastParms(1);
      document.addEventListener(EIMIT, menuItemOnClick);
      window.addEventListener('popstate', handlePopState);
      return () => {
        document.removeEventListener(EIMIT, menuItemOnClick);
        window.removeEventListener('popstate', handlePopState);
      };
    }, []);

    useEffect(() => {
      if (data.length) {
        getCurrentTable?.(data);
      }
    }, [data, getCurrentTable]);

    useEffect(() => {
      clearInterval(intervalTimer.current);
      intervalTimer.current = null;
      if (longPolling) {
        intervalTimer.current = setInterval(() => {
          getData({}, false, false);
        }, 5000);
      }
      return () => clearInterval(intervalTimer.current);
    }, [longPolling]);

    useImperativeHandle(ref, () => ({
      handleClick,
      getTableData: () => data,
      afreshTableData: (cparams, isRest) => {
        getData(cparams, isRest);
      },
    }));

    const handleCancel = () => {
      setColumnsKeys([...lastKeys]);
      setOpen(false);
    };

    const handleConfirm = () => {
      if (!columnsKeys.length) {
        message.warning(t('hint.selectRow'));
        return;
      }
      setLoadingConfirm(true);
      updateTableHeaderConfig({
        type: editTableHeaderUrl,
        body: columnsKeys,
      })
        .then(() => {
          message.success(t('success'));
          setLastKeys([...columnsKeys]);
          setOpen(false);
          setLoadingConfirm(false);
        })
        .catch(() => {
          setLoadingConfirm(false);
        });
    };

    const getTableColumns: ProColumns[] = useMemo(() => {
      if (!editTableHeaderUrl || !isDefaultToolBar) {
        return (
          columns?.map((item) =>
            item.dataIndex === 'action' ? { ...item, ...action } : item,
          ) || []
        );
      }

      const cols: any[] = [];
      tableColumns.map(({ key, name }) => {
        if (!columnsKeys.includes(key as Key)) return;
        const hostyCol = tableColsRender?.find((col) => key === col.dataIndex);
        cols.push({
          render: (text: any) => (
            <>{DATEFILDS.includes(key.toString()) ? getTimeToString(text) : text}</>
          ),
          ...hostyCol,
          title: hostyCol?.title || name,
          dataIndex: key?.split('.'),
        });
      });
      const actFiled = tableColsRender?.find((col) => col.dataIndex === 'action');
      if (actFiled) {
        cols.push({ ...actFiled, ...action });
      }
      return cols;
    }, [
      columns,
      columnsKeys,
      tableColumns,
      tableColsRender,
      action,
      editTableHeaderUrl,
      isDefaultToolBar,
    ]);

    const getFilterComponet = useMemo(() => {
      if (!filterTableFields) return null;
      return (
        <FilterComponet
          className="!mb-[2px]"
          fieldsProp={filterTableFields}
          isAddParamsToUrl={isAddParamsToUrl}
          submit={(values) => {
            reastParms(1, isAddParamsToUrl ? {} : values);
            filterSubmit?.(isAddParamsToUrl ? getPathParams() : values);
          }}
          reset={() => {
            reastParms(1);
            resetSubmit?.();
          }}
        />
      );
    }, [filterTableFields, isAddParamsToUrl]);

    const loadingPage = useMemo(() => {
      return loading || loadTableCols;
    }, [loadTableCols, loading]);

    return (
      <>
        {getFilterComponet}
        <div
          className={clsx(
            'bg-[#fff] dark:bg-[#141414] rounded-[2px] min-h-[calc(100vh-166px)] p-4',
            className,
          )}
        >
          {(headerTitle || tableHeader) && (
            <div className="pb-4 flex items-center justify-between">
              <div className="w-auto text-[16px] font-medium">
                {headerTitle?.({ node: tableRef, dataSource: data })}
              </div>
              <div>
                {tableHeader?.(tableRef)}
                {isDefaultToolBar && (
                  <>
                    <ExportFile
                      disabled={!data.length}
                      isIconAndText={false}
                      onClick={() => {
                        const path = new URL(window.location.href);
                        const downurl = new URLSearchParams({
                          ...getPathParams(),
                          ...otherParams,
                        }).toString();
                        const otherPar = requestUrl?.split('?');
                        const otherUrl =
                          otherPar && otherPar.length > 1 ? otherPar[1] : '';
                        const exUrl = exportUrl || `/api/${tableName}/export`;
                        const downString = `${path.origin}${exUrl}?${downurl}${
                          otherUrl ? `&${otherUrl}` : ''
                        }`;
                        window.open(downString);
                      }}
                    />
                    <Popover content={<p>{t('preference')}</p>} title="">
                      <Button
                        color="default"
                        className="ml-3"
                        icon={
                          <SettingOutlined
                            className="text-[#666] dark:text-[#ffffffd9]"
                            style={{ fontSize: 20 }}
                          />
                        }
                        onClick={() => setOpen(true)}
                        variant="link"
                      />
                    </Popover>
                  </>
                )}
              </div>
            </div>
          )}
          <Modal
            title={<p className="pt-5 font-normal">{t('selectCols')}</p>}
            width={600}
            footer={
              <>
                <Button type="default" variant="outlined" onClick={handleCancel}>
                  {t('cancel')}
                </Button>
                <Button
                  type="primary"
                  disabled={!columnsKeys.length}
                  loading={loadingConfirm}
                  onClick={handleConfirm}
                >
                  {t('confirm')}
                </Button>
              </>
            }
            open={open}
            closeIcon={
              <CloseOutlined
                className="text-[#000000d9] dark:text-[#ffffff73]"
                style={{ fontSize: 24 }}
              />
            }
            onCancel={handleCancel}
          >
            <Table
              rowSelection={{
                selectedRowKeys: columnsKeys,
                onChange: (rowKeys) => {
                  if (!rowKeys.length) {
                    message.warning(t('hint.selectRow'));
                  }
                  setColumnsKeys(rowKeys);
                },
                getCheckboxProps: (record: TableHeaderCol) => ({
                  disabled: !record.detectable,
                }),
              }}
              columns={[{ title: '', dataIndex: 'name' }]}
              showHeader={false}
              scroll={{ y: 600 }}
              pagination={false}
              rowKey="key"
              rowClassName="setHostiyRow"
              dataSource={tableColumns}
            />
          </Modal>
          <ProTable
            search={false}
            actionRef={tableRef}
            columns={getTableColumns}
            options={false}
            bordered={bordered}
            rowKey={rowKey}
            loading={loadingPage}
            rowHoverable
            scroll={{
              x: 'max-content',
              y: data.length ? tableHeight : undefined,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  onCilickRow?.(record);
                },
              };
            }}
            dataSource={data}
            columnEmptyText=" "
            footer={
              footer
                ? () => {
                    return (
                      <span>
                        {footer}
                        {pageParams.total || data.length}
                      </span>
                    );
                  }
                : undefined
            }
            className="border-[#F1F0F5] dark:border-[#ffffff21] border-[1px] cumoutProTable"
            pagination={
              isPagination
                ? {
                    ...PaginationConfig(),
                    current: pageParams.current,
                    total: pageParams.total,
                    pageSize: pageParams.size,
                    onChange(page) {
                      reastParms(page);
                    },
                  }
                : false
            }
            rowSelection={
              isShowCheckbox
                ? {
                    ...rowSelection,
                    selectedRowKeys,
                    onChange: (rowKeys, selectedRows) => {
                      onChangeSelected?.(rowKeys, selectedRows);
                      setSelectedRowKeys(rowKeys);
                    },
                  }
                : false
            }
          />
        </div>
      </>
    );
  },
);

export default BasicTable;
